import { format, isValid, parseISO } from 'date-fns';
import { CalendarClock, MessageSquareWarning } from 'lucide-react-native';

import type { ChangedRecentlyItem } from '../../model/types';
import { REPLY_DRAFT_COPY } from '../copy';
import { Disclosure } from '../shared/Disclosure';
import { SourceLink } from '../shared/SourceLink';

import { Alert, AlertIcon, AlertText } from '@/components/ui/alert';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';

function formatWhen(value: string, pattern: string): string {
  const date = parseISO(value);

  return isValid(date) ? format(date, pattern) : value;
}

/**
 * Says the asker has written since the draft was prepared, on the card and
 * while editing, so the expert does not choose or send from a stale case. The
 * icon and the words carry it; the warning tint only supports them.
 */
export function CaseUpdatedNotice({ askerFollowedUpAt }: { askerFollowedUpAt: string }) {
  return (
    <Alert
      action="warning"
      className="items-start"
      testID="reply-draft-case-updated"
      accessibilityLiveRegion="polite">
      <AlertIcon as={MessageSquareWarning} className="mt-0.5" />
      <AlertText className="flex-1">
        {REPLY_DRAFT_COPY.caseUpdated(`on ${formatWhen(askerFollowedUpAt, "MMM d 'at' h:mm a")}`)}
      </AlertText>
    </Alert>
  );
}

/** Newest first, so the updates most likely to postdate the model lead. */
function newestFirst(items: ChangedRecentlyItem[]): ChangedRecentlyItem[] {
  const time = (item: ChangedRecentlyItem) => {
    const date = item.date ? parseISO(item.date) : null;

    return date && isValid(date) ? date.getTime() : -Infinity;
  };

  return [...items].sort((first, second) => time(second) - time(first));
}

/**
 * What changed in the evidence lately, as a section under the question beside
 * the expert's own history, so both can be opened while choosing. Its row
 * carries the count and a calendar mark, because it is how the expert learns
 * of an approval newer than the model's knowledge and should not read as just
 * more background. Open, it lists every update in full, newest first, each
 * with its date and source and a link to the page behind it when there is one.
 */
export function ChangedRecently({ items }: { items: ChangedRecentlyItem[] }) {
  return (
    <Disclosure
      label={REPLY_DRAFT_COPY.changedRecentlyHeading(items.length)}
      icon={CalendarClock}
      emptyText={items.length === 0 ? REPLY_DRAFT_COPY.changedRecentlyEmpty : null}
      testID="reply-draft-changed-recently">
      <VStack className="gap-3">
        {newestFirst(items).map((item, index) => (
          <VStack key={`${item.text}-${index}`} className="gap-0.5">
            <Text size="sm" className="text-typography-900">
              {item.text}
            </Text>
            {item.date || item.source ? (
              <Text size="xs" className="text-typography-700">
                {[item.date ? formatWhen(item.date, 'MMM d, yyyy') : null, item.source]
                  .filter(Boolean)
                  .join(' · ')}
              </Text>
            ) : null}
            {item.url ? (
              <SourceLink
                url={item.url}
                publicationId={item.publicationId}
                title={item.text}
                from="changed_recently"
                text={REPLY_DRAFT_COPY.viewDetails}
                accessibilityLabel={REPLY_DRAFT_COPY.viewDetailsLabel(item.text)}
                testID={`reply-draft-changed-recently-link-${index}`}
              />
            ) : null}
          </VStack>
        ))}
      </VStack>
    </Disclosure>
  );
}
